import { Controller } from '@hotwired/stimulus';
import SortableList from 'stimulus/sortable_list';

const ITEM_IDENTIFIER = 'accordion-item-component';
const MOVABLE_SELECTOR = '.ui-accordion-item--movable';
const HEADER_SELECTOR = '.ui-accordion-item__header';
const TITLE_SELECTOR = '.ui-accordion-item__title';
const BODY_SELECTOR = '.ui-accordion-item__body';
const CLONE_CLASS = 'ui-accordion-item--drag-clone';
const NESTED_SORTABLE_SELECTOR = '[data-controller~="draggable-list-component"]';

// Coordinates the items: keeps a single one open when `exclusive`, and hands
// the badge numbering and — when `sortable` — the reordering to SortableList,
// which it teaches where its items, badges, titles and live region are. The
// item's header is both the drag handle and the keyboard's, so Alt + up/down
// moves whichever item holds focus there. A move that changes the order
// dispatches `accordion-component:reorder` with the moved item, its old and
// new index and the ordered item ids.
export default class extends Controller {
  static targets = ['item', 'number', 'status'];

  static values = {
    exclusive: { type: Boolean, default: true },
    sortable: { type: Boolean, default: false },
    numberBase: { type: Number, default: 1 }
  };

  connect() {
    this.list = new SortableList({
      element: this.element,
      drag: this.sortableValue,
      movableSelector: MOVABLE_SELECTOR,
      handle: HEADER_SELECTOR,
      chosenClass: 'ui-accordion-item--dragging',
      ghostClass: 'ui-accordion-item--ghost',
      // An item's form can hold a draggable list of its own (a step's interview
      // script), which is the one thing that has to stand down for the drag.
      // The clone that trails the cursor is stripped too, body included — the
      // drag never shows it.
      fallbackClass: CLONE_CLASS,
      hideInClone: BODY_SELECTOR,
      parkSelector: NESTED_SORTABLE_SELECTOR,
      ignoreAttribute: `data-${this.identifier}-ignore`,
      rows: () => this.itemTargets,
      badgeFor: (item) => this.badgeOf(item),
      titleFor: (item) => item.querySelector(TITLE_SELECTOR)?.textContent.trim() || 'Item',
      status: () => (this.hasStatusTarget ? this.statusTarget : null),
      numberBase: () => this.numberBaseValue,
      onReorder: (detail) => this.dispatch('reorder', { detail })
    });
  }

  disconnect() {
    this.list?.destroy();
    this.list = null;
  }

  itemTargetConnected() {
    this.list?.scheduleRenumber();
  }

  itemTargetDisconnected() {
    this.list?.scheduleRenumber();
  }

  reorderKeydown(event) {
    if (!this.sortableValue) return;

    this.list?.reorderKeydown(event, (keyEvent) => {
      const header = keyEvent.target.closest(HEADER_SELECTOR);
      const row = header?.closest(MOVABLE_SELECTOR);

      return row ? { row, focus: header } : null;
    });
  }

  // Wired from `accordion-item-component:open` bubbling up from an item.
  itemOpened(event) {
    if (!this.exclusiveValue) return;

    this.itemTargets
      .filter((item) => item !== event.target)
      .forEach((item) => this.itemController(item)?.close());
  }

  // A nested accordion's badges belong to its own controller, so only the ones
  // this element owns take a position here.
  badgeOf(item) {
    const badge = item.querySelector(`[data-${this.identifier}-target~="number"]`);
    if (!badge) return null;

    return badge.closest(`[data-controller~="${this.identifier}"]`) === this.element ? badge : null;
  }

  itemController(item) {
    return this.application.getControllerForElementAndIdentifier(item, ITEM_IDENTIFIER);
  }
}
